import React, { useEffect, useState } from "react";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import { useTranslation } from "react-i18next";
import { useUnifiedAudio } from "../../../../context/juno/UnifiedAudioContext";
import { useVoiceContext } from "../../../../context/VoiceContext";
import { useSound } from "../../../../context/juno/SoundContext";
import AudioTimeDisplay from "../Music/components/AudioTimeDisplay";

// プレーヤーの種類
export type PlayerType = "voice" | "music";

interface UnifiedWaveformPlayerProps {
  playerType: PlayerType;
}

// 統合波形プレーヤーコンポーネント
const UnifiedWaveformPlayer: React.FC<UnifiedWaveformPlayerProps> = ({ playerType }) => {
  const { t } = useTranslation();
  const [previousPlayerType, setPreviousPlayerType] = useState<PlayerType>(playerType);

  // 各コンテキストの取得
  const voiceContext = useVoiceContext();
  const soundContext = useSound();

  // 統合オーディオコンテキストの使用
  const {
    currentAudioType,
    isAnyPlaying,
    currentVoice,
    currentSound,
    playVoice,
    stopVoice,
    playSound,
    stopSound,
    previousAudio,
    nextAudio,
    toggleMute,
  } = useUnifiedAudio();

  // 現在の再生状態に基づく計算プロパティ
  const isVoicePlaying = currentAudioType === "voice" && isAnyPlaying;
  const isSoundPlaying = currentAudioType === "music" && isAnyPlaying;

  // コンテンツの有無を確認
  const hasVoiceContent = currentVoice !== null;
  const hasMusicContent = currentSound !== null;
  const hasContent = hasVoiceContent || hasMusicContent;

  // 表示するコンテンツのタイプ（再生中のものを優先）
  const displayType = isVoicePlaying ? "voice" : isSoundPlaying ? "music" : playerType;

  // 表示するアイコン
  const displayIcon =
    displayType === "voice" ? (
      <KeyboardVoiceIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
    ) : (
      <MusicNoteIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
    );

  // 表示する名前
  const displayName =
    displayType === "voice"
      ? currentVoice?.text || ""
      : currentSound?.params?.prompt
        ? String(currentSound.params.prompt)
        : "";

  // 再生/停止を処理する関数
  const handlePlayback = () => {
    if (displayType === "voice") {
      if (isVoicePlaying) {
        stopVoice();
      } else if (currentVoice && currentVoice.uuid) {
        playVoice(currentVoice.uuid);
      }
    } else {
      if (isSoundPlaying) {
        stopSound();
      } else if (currentSound && currentSound.url) {
        playSound(currentSound.url);
      }
    }
  };

  // 前の項目へ - 統合コンテキストのメソッドを使用
  const handlePrevious = () => {
    if (hasContent) {
      // 現在のコンテンツがある場合のみ実行
      previousAudio();
    }
  };

  // 次の項目へ - 統合コンテキストのメソッドを使用
  const handleNext = () => {
    if (hasContent) {
      // 現在のコンテンツがある場合のみ実行
      nextAudio();
    }
  };

  // ミュート切り替え - 統合コンテキストのメソッドを使用
  const handleMute = () => {
    if (hasContent) {
      // 現在のコンテンツがある場合のみ実行
      toggleMute();
    }
  };

  // タブ切り替え時の処理
  useEffect(() => {
    if (previousPlayerType !== playerType) {
      setPreviousPlayerType(playerType);
    }
  }, [playerType, previousPlayerType]);

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
        p: 1,
        px: { xs: 2, sm: 4 },
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          flex: 2,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {/* 音声/音楽情報 */}
        <Box
          sx={{
            width: { xs: "100%", sm: 200 },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* 再生中のコンテンツに対応するアイコンを表示 */}
          {hasContent && displayIcon}
          <Typography variant="body2" noWrap gutterBottom>
            {hasContent ? displayName : displayType === "voice" ? t("voice.noPlayingVoice") : "No sound playing"}
          </Typography>
        </Box>

        {/* コントロール */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <IconButton onClick={handlePrevious} disabled={!hasContent} size="small">
            <SkipPreviousIcon />
          </IconButton>
          <IconButton
            onClick={handlePlayback}
            disabled={!hasContent} // コンテンツがある場合のみ有効
            size="small"
            sx={{ minWidth: "40px" }}
          >
            {isAnyPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <IconButton onClick={handleNext} disabled={!hasContent} size="small">
            <SkipNextIcon />
          </IconButton>
          <IconButton onClick={handleMute} disabled={!hasContent} size="small">
            <VolumeUpIcon />
          </IconButton>
        </Box>

        {/* 時間表示（別コンポーネント） */}
        <AudioTimeDisplay
          wavesurferInstance={
            displayType === "voice" ? voiceContext.wavesurferInstance : soundContext.wavesurferInstance
          }
          isPlaying={isAnyPlaying}
        />

        {/* 波形表示 - 音声タイプごとに別々の表示領域を用意 */}
        {/* Voice波形表示 - 再生中または表示対象の場合に表示 */}
        <Box
          ref={voiceContext.waveformRef}
          onClick={(e) => {
            if (!voiceContext.wavesurferInstance) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const position = (e.clientX - rect.left) / rect.width;
            voiceContext.seekTo(position);
          }}
          sx={{
            width: "100%",
            height: 40,
            cursor: "pointer",
            overflow: "hidden",
            display: displayType === "voice" ? "block" : "none",
            "& > wave": {
              height: "100% !important",
            },
          }}
        />

        {/* Sound波形表示 - 再生中または表示対象の場合に表示 */}
        <Box
          ref={soundContext.waveformRef}
          onClick={(e) => {
            if (!soundContext.wavesurferInstance) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const position = (e.clientX - rect.left) / rect.width;
            soundContext.seekTo(position);
          }}
          sx={{
            width: "100%",
            height: 40,
            cursor: "pointer",
            overflow: "hidden", // 波形がはみ出ないようにする
            display: displayType === "music" ? "block" : "none", // Music表示の場合のみ表示
            "& > wave": {
              // wavesurferの内部要素にスタイルを適用
              height: "100% !important", // 高さを親コンテナに合わせる
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default UnifiedWaveformPlayer;
